import { useTranslation } from 'react-i18next'

import { toast } from '@/components/ui/toast'
import {
  callClient as callClientAction,
  emailClient as emailClientAction,
  navigateToAddress as navigateToAddressAction,
  whatsappClient as whatsappClientAction,
  type IntegrationFailureReason,
  type IntegrationResult,
  type NavigateDestination,
} from '@/lib/integrations'

// Maps a pure integration failure to its translation key in the `errors`
// namespace. Centralised here so the three quick-action pages don't each repeat
// the reason → message wiring.
const FAILURE_MESSAGE_KEY: Record<IntegrationFailureReason, string> = {
  'no-phone': 'integrations.noPhone',
  'no-email': 'integrations.noEmail',
  'no-address': 'integrations.noAddress',
}

// Thin UI wrapper over the pure `@/lib/integrations` helpers: runs the action and
// surfaces a destructive toast when the contact is missing. Returns the helper's
// result so callers can branch further if needed.
export function useQuickActions() {
  const { t } = useTranslation('errors')

  const notify = (result: IntegrationResult) => {
    if (!result.ok) {
      toast({
        variant: 'destructive',
        title: t(FAILURE_MESSAGE_KEY[result.reason]),
      })
    }
    return result
  }

  return {
    callClient: (phone: string | null | undefined) =>
      notify(callClientAction(phone)),
    whatsappClient: (phone: string | null | undefined) =>
      notify(whatsappClientAction(phone)),
    emailClient: (email: string | null | undefined) =>
      notify(emailClientAction(email)),
    navigateToAddress: (
      destination: NavigateDestination | string | null | undefined
    ) => notify(navigateToAddressAction(destination)),
  }
}
